/**
 * src/lib/voice/dictationSafety.js — TICKET-936.
 *
 * The text half of the dictation durability contract (the audio half is the
 * IndexedDB draft in useDictation, TICKET-324). Every transcript is written
 * here the moment it exists, BEFORE the audio draft is deleted, and stays until
 * the message is sent or the user discards it. So a button that clears or
 * replaces the composer (mode chip, "Fill every bundle", a suggestion, a failed
 * turn, the slash menu, closing the chat mid-transcription) can no longer
 * destroy the only copy. The composer shows what is missing from its box and
 * offers it back (useDictationSafetyNet + DictationRestoreBar).
 *
 * localStorage, not IndexedDB: a transcript is small, and the store must be
 * readable synchronously on mount. Fails soft — no storage, no safety net,
 * dictation still works.
 */
import { logVoiceEvent } from './voiceLogger.js';

export const SAFETY_KEY = 'acn_dictation_safety_v1';
export const SAFETY_EVENT = 'acn:dictation-safety';
const MAX_ENTRIES = 5;
const TTL_MS = 7 * 864e5;

// The safety net failing is itself a loss risk — say so on the spine.
const warn = (reason, err) => {
  try { logVoiceEvent('dictation_safety_store_error', 'error', `dictation safety store ${reason}: ${err?.message}`, { source: 'client', module: 'voice', reason }); }
  catch { /* spine-emit-ok: telemetry must never break dictation */ }
};

function read() {
  try {
    const raw = JSON.parse(window.localStorage.getItem(SAFETY_KEY) || '[]');
    return Array.isArray(raw) ? raw.filter((e) => e && e.id && e.text && Date.now() - e.at < TTL_MS) : [];
  } catch (err) {
    // spine-emit-ok: warn() emits dictation_safety_store_error via logVoiceEvent
    warn('read', err);
    return [];
  }
}

function write(entries) {
  try { window.localStorage.setItem(SAFETY_KEY, JSON.stringify(entries)); } catch (err) { warn('write', err); /* spine-emit-ok: warn() emits via logVoiceEvent */ }
  try { window.dispatchEvent(new Event(SAFETY_EVENT)); } catch { /* spine-emit-ok: no window to notify, the store is already written */ }
}

const newId = () => (typeof crypto !== 'undefined' && crypto.randomUUID
  ? crypto.randomUUID() : `dict-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`);

/** Save a transcript for a composer scope ('chat'). Returns the entry id, or null for empty text. */
export function rememberDictation(text, scope) {
  const clean = String(text || '').trim();
  if (!clean || typeof window === 'undefined') return null;
  const entry = { id: newId(), text: clean, scope, at: Date.now() };
  // ponytail: last 5 per device; a sixth unsent dictation pushes the oldest out.
  write([entry, ...read()].slice(0, MAX_ENTRIES));
  return entry.id;
}

/** Newest first. */
export function listSafeDictations(scope) {
  if (typeof window === 'undefined') return [];
  return read().filter((e) => e.scope === scope).sort((a, b) => b.at - a.at);
}

export function forgetDictation(id) {
  if (typeof window === 'undefined') return;
  write(read().filter((e) => e.id !== id));
}

const words = (s) => String(s || '').toLowerCase().match(/[\p{L}\p{N}]+/gu) || [];

/**
 * Is this dictation still in the box? Word overlap, not exact match, so fixing
 * a misheard word does not make it look "lost". 60 % of its words present = yes.
 */
export function isPresent(text, input) {
  const want = words(text);
  if (!want.length) return true;
  const have = new Set(words(input));
  const hit = want.filter((w) => have.has(w)).length;
  return hit / want.length >= 0.6;
}
